/**
 * Injects mega menu rows from #obor-mega-menu-source into column mounts.
 */

/** @param {HTMLElement} headerComponent */
function setFullOpenHeaderHeight(headerComponent, submenuHeight) {
  const isOverlap = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
  const headerVisibleHeight =
    isOverlap && headerComponent.offsetHeight > 0
      ? headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0
      : headerComponent.offsetHeight;

  if (submenuHeight <= 0) {
    headerComponent.style.setProperty('--submenu-height', '0px');
    headerComponent.style.setProperty('--full-open-header-height', '0px');
    return;
  }

  headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
  headerComponent.style.setProperty(
    '--full-open-header-height',
    `${submenuHeight + headerVisibleHeight}px`
  );
}

/** Recalculate header clip height after async row injection. */
function syncOborMegaMenuSubmenuHeight() {
  const headerComponent = document.getElementById('header-component');
  if (!headerComponent) return;

  const openSubmenus = document.querySelectorAll(
    '.menu-list__list-item:has([aria-expanded="true"]) > .menu-list__submenu:has(.obor-mega-menu, .obor-info-menu, .obor-racing-menu)'
  );

  if (!openSubmenus.length) return;

  let maxHeight = 0;
  openSubmenus.forEach((submenu) => {
    if (!(submenu instanceof HTMLElement)) return;

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const panel = submenu.querySelector('.obor-mega-menu');
    const measured =
      inner?.scrollHeight ||
      panel?.scrollHeight ||
      inner?.offsetHeight ||
      panel?.offsetHeight ||
      submenu.offsetHeight;

    maxHeight = Math.max(maxHeight, measured);
  });

  if (maxHeight > 0) {
    setFullOpenHeaderHeight(headerComponent, maxHeight);
  }
}

function scheduleOborSubmenuHeightSync() {
  requestAnimationFrame(() => {
    requestAnimationFrame(syncOborMegaMenuSubmenuHeight);
  });
}

/** @type {WeakSet<HTMLElement>} */
const oborMegaGridResizeObserved = new WeakSet();

/** @param {HTMLElement} grid */
function observeOborMegaGridResize(grid) {
  if (oborMegaGridResizeObserved.has(grid)) return;
  oborMegaGridResizeObserved.add(grid);
  const resizeObserver = new ResizeObserver(() => {
    scheduleOborSubmenuHeightSync();
  });
  resizeObserver.observe(grid);
}

function mountOborMegaMenuRows() {
  const source = document.getElementById('obor-mega-menu-source');
  if (!source || !source.children.length) return;

  document.querySelectorAll('[data-obor-mega-grid]').forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;
    observeOborMegaGridResize(grid);
    if (grid.dataset.oborMegaMounted === 'true') return;

    [...source.children].forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      const column =
        node.dataset.column ||
        node.querySelector('[data-column]')?.getAttribute('data-column');

      if (!column) return;

      const mount = grid.querySelector(`[data-obor-column="${column}"]`);
      if (!mount) return;

      mount.appendChild(node.cloneNode(true));
    });

    grid.dataset.oborMegaMounted = 'true';
    scheduleOborSubmenuHeightSync();
  });
}

function scheduleOborMegaMenuMount() {
  mountOborMegaMenuRows();
  requestAnimationFrame(mountOborMegaMenuRows);
  setTimeout(mountOborMegaMenuRows, 0);
  setTimeout(mountOborMegaMenuRows, 400);
  setTimeout(mountOborMegaMenuRows, 1200);
  scheduleOborSubmenuHeightSync();
  setTimeout(scheduleOborSubmenuHeightSync, 500);
  setTimeout(scheduleOborSubmenuHeightSync, 1300);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleOborMegaMenuMount);
} else {
  scheduleOborMegaMenuMount();
}

document.addEventListener('shopify:section:load', scheduleOborMegaMenuMount);

const headerSection = document.querySelector('.header-section');
if (headerSection) {
  const hydrationObserver = new MutationObserver(() => {
    if (headerSection.dataset.hydrated === 'true') {
      document.querySelectorAll('[data-obor-mega-grid]').forEach((grid) => {
        delete grid.dataset.oborMegaMounted;
        grid.querySelectorAll('[data-obor-column]').forEach((mount) => {
          mount.replaceChildren();
        });
      });
      scheduleOborMegaMenuMount();
    }
  });
  hydrationObserver.observe(headerSection, {
    attributes: true,
    attributeFilter: ['data-hydrated'],
  });
}

/** Toggle body class so hero carousel controls stay under open header menus. */
function syncHeaderMenuOpenClass() {
  const menuOpen =
    document.querySelector('header-menu[aria-expanded="true"]') != null ||
    document.querySelector(
      '.menu-list__submenu[data-active]:has(.obor-mega-menu, .obor-info-menu, .obor-racing-menu)'
    ) !=
      null;

  document.body.classList.toggle('obor-header-menu-open', menuOpen);
}

const headerComponent = document.getElementById('header-component');
if (headerComponent) {
  syncHeaderMenuOpenClass();
  const menuObserver = new MutationObserver(() => {
    document.querySelectorAll('[data-obor-mega-grid]').forEach((grid) => {
      const vehicleMount = grid.querySelector('[data-obor-column="vehicle"]');
      if (vehicleMount && !vehicleMount.children.length) {
        delete grid.dataset.oborMegaMounted;
        grid.querySelectorAll('[data-obor-column]').forEach((mount) => {
          mount.replaceChildren();
        });
        mountOborMegaMenuRows();
        scheduleOborSubmenuHeightSync();
      }
    });
  });
  menuObserver.observe(headerComponent, { childList: true, subtree: true });

  headerComponent.addEventListener('pointerenter', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.menu-list__submenu:has(.obor-mega-menu, .obor-info-menu, .obor-racing-menu)'))
      return;
    scheduleOborSubmenuHeightSync();
  });

  const headerMenu = headerComponent.querySelector('header-menu');
  if (headerMenu) {
    const submenuHeightObserver = new MutationObserver(() => {
      scheduleOborSubmenuHeightSync();
      syncHeaderMenuOpenClass();
    });
    submenuHeightObserver.observe(headerMenu, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-expanded', 'data-active'],
    });
  }

  document.addEventListener(
    'pointerenter',
    (event) => {
      if (event.target instanceof Element && event.target.closest('header-menu')) {
        syncHeaderMenuOpenClass();
      }
    },
    true
  );

  document.addEventListener(
    'pointerleave',
    (event) => {
      if (event.target instanceof Element && event.target.closest('header-menu')) {
        requestAnimationFrame(syncHeaderMenuOpenClass);
      }
    },
    true
  );
}
